import React, { useEffect, useState } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { ScitusLogo } from "./ScitusLogo";

const PRIMARY = [
    { path: "/products", label: "Products" },
    { path: "/industries", label: "Industries" },
    { path: "/pricing", label: "Pricing" },
];

const RESOURCES = [
    { path: "/stories", label: "Customer stories" },
    { path: "/blog", label: "Insights" },
    { path: "/compare", label: "Compare" },
    { path: "/integrations", label: "Integrations" },
    { path: "/changelog", label: "Changelog" },
];

const RESOURCE_KEYS = RESOURCES.map((r) => r.path.replace(/^\//, ""));

export const Navbar = ({ current }) => {
    const [open, setOpen] = useState(false);
    const [resourcesOpen, setResourcesOpen] = useState(false);
    const closeTimerRef = React.useRef(null);

    const cancelClose = () => {
        if (closeTimerRef.current) {
            clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
    };
    const scheduleClose = () => {
        cancelClose();
        closeTimerRef.current = setTimeout(() => setResourcesOpen(false), 220);
    };

    // Close menu / dropdown whenever the app navigates (history pushState or popstate).
    useEffect(() => {
        const close = () => { setOpen(false); setResourcesOpen(false); };
        window.addEventListener("popstate", close);
        window.addEventListener("scitus:navigate", close);
        return () => {
            window.removeEventListener("popstate", close);
            window.removeEventListener("scitus:navigate", close);
        };
    }, []);

    const isActive = (path) => {
        const target = path.replace(/^\//, "");
        return current === target;
    };

    return (
        <header className="nav-root" data-testid="navbar">
            <div className="container nav-inner">
                <a href="/" aria-label="Scitus.ai home" data-testid="nav-logo-link">
                    <ScitusLogo height={28} />
                </a>

                <nav className="nav-links" aria-label="Primary">
                    {PRIMARY.map((l) => (
                        <a
                            key={l.path}
                            href={l.path}
                            className={`nav-link ${isActive(l.path) || (l.path === "/products" && current === "product-detail") || (l.path === "/industries" && current === "industry-detail") ? "active" : ""}`}
                            data-testid={`nav-link-${l.label.toLowerCase()}`}
                        >
                            {l.label}
                        </a>
                    ))}
                    <div
                        className="nav-resources"
                        onMouseEnter={() => { cancelClose(); setResourcesOpen(true); }}
                        onMouseLeave={scheduleClose}
                    >
                        <button
                            type="button"
                            className={`nav-link ${RESOURCE_KEYS.includes(current) || current === "case-study" || current === "blog-post" || current === "compare-detail" ? "active" : ""}`}
                            aria-expanded={resourcesOpen}
                            onClick={() => setResourcesOpen((v) => !v)}
                            data-testid="nav-link-resources"
                        >
                            Resources <ChevronDown size={14} />
                        </button>
                        {resourcesOpen && (
                            <div className="nav-dropdown" data-testid="nav-dropdown-resources">
                                {RESOURCES.map((r) => (
                                    <a
                                        key={r.path}
                                        href={r.path}
                                        className="nav-dropdown-item"
                                        data-testid={`nav-dropdown-${r.label.toLowerCase().replace(/\s+/g, "-")}`}
                                    >
                                        {r.label}
                                    </a>
                                ))}
                            </div>
                        )}
                    </div>
                    <a href="/contact" className={`nav-link ${isActive("/contact") ? "active" : ""}`} data-testid="nav-link-contact">
                        Contact
                    </a>
                </nav>

                <a href="/contact" className="btn btn-ghost btn-sm nav-cta" data-testid="nav-book-demo-btn">
                    Book a Demo
                </a>

                <button
                    type="button"
                    className="nav-mobile-toggle"
                    onClick={() => setOpen((v) => !v)}
                    aria-label={open ? "Close menu" : "Open menu"}
                    aria-expanded={open}
                    data-testid="nav-mobile-toggle"
                >
                    {open ? <X size={20} /> : <Menu size={20} />}
                </button>
            </div>

            {open && (
                <div className="nav-drawer" data-testid="nav-drawer">
                    {PRIMARY.concat([{ path: "/contact", label: "Contact" }]).map((l) => (
                        <a
                            key={l.path}
                            href={l.path}
                            className="nav-link"
                            onClick={() => setOpen(false)}
                            data-testid={`nav-drawer-link-${l.label.toLowerCase()}`}
                        >
                            {l.label}
                        </a>
                    ))}
                    <div className="nav-drawer-section">Resources</div>
                    {RESOURCES.map((r) => (
                        <a
                            key={r.path}
                            href={r.path}
                            className="nav-link"
                            onClick={() => setOpen(false)}
                            data-testid={`nav-drawer-link-${r.label.toLowerCase().replace(/\s+/g, "-")}`}
                            data-resource-testid={`nav-dropdown-${r.label.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                            <span data-testid={`nav-dropdown-${r.label.toLowerCase().replace(/\s+/g, "-")}`} style={{ display: "contents" }}>
                                {r.label}
                            </span>
                        </a>
                    ))}
                    <a href="/contact" className="btn btn-primary" onClick={() => setOpen(false)} data-testid="nav-drawer-book-demo-btn">
                        Book a Demo
                    </a>
                </div>
            )}
        </header>
    );
};

export default Navbar;
